/**
 * MineralReadouts — persistent world-space labels showing the LIVE remaining
 * minerals of every body currently being mined (hull + drones). Reconciled by id
 * each frame so several asteroids being mined at once each show their own count.
 */
import { Container, Text } from "pixi.js";

export interface Readout {
  id: string;
  x: number;
  y: number;
  ore: number;
}

export class MineralReadouts {
  readonly view = new Container();
  private pool: Text[] = [];
  private active = new Map<string, Text>();

  sync(items: readonly Readout[]): void {
    const present = new Set<string>();
    for (const it of items) {
      present.add(it.id);
      let t = this.active.get(it.id);
      if (!t) {
        t = this.pool.pop();
        if (!t) {
          t = new Text({
            text: "",
            style: {
              fontFamily: "system-ui, Arial, sans-serif",
              fontSize: 13,
              fontWeight: "700",
              fill: 0x9fe6ff,
              stroke: { color: 0x05070f, width: 3 },
            },
          });
          t.anchor.set(0.5, 1);
        }
        t.visible = true;
        this.view.addChild(t);
        this.active.set(it.id, t);
      }
      t.text = `⛏ ${fmt(it.ore)}`;
      t.position.set(it.x, it.y);
    }
    for (const [id, t] of this.active) {
      if (!present.has(id)) {
        t.visible = false;
        this.view.removeChild(t);
        this.pool.push(t);
        this.active.delete(id);
      }
    }
  }

  clear(): void {
    for (const t of this.active.values()) {
      t.visible = false;
      this.view.removeChild(t);
      this.pool.push(t);
    }
    this.active.clear();
  }
}

function fmt(n: number): string {
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "k";
  return Math.ceil(n).toString();
}
